import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFilePdf, faCircleCheck, faTriangleExclamation, faXmark
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../context/AuthContext';
import { parseOfferingsFile, confirmOfferingsImport, getMinistries } from '../services/api';
import { formatAccountingCurrency } from '../utils/formatters';
import './ImportOfferings.css';

const ImportOfferings = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  
  // States
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [parsedData, setParsedData] = useState(null);
  const [editableData, setEditableData] = useState([]);
  const [ministries, setMinistries] = useState([]);
  const [period, setPeriod] = useState({ month: new Date().getMonth() + 1, year: new Date().getFullYear() });
  const [associationName, setAssociationName] = useState('');
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isAdmin) {
      navigate('/');
      return;
    }
    loadMinistries();
  }, [isAdmin, navigate]);

  const loadMinistries = async () => {
    try {
      const response = await getMinistries(new Date().getFullYear());
      setMinistries(response.data);
    } catch (error) {
      console.error('Erro ao carregar ministérios:', error);
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile.type !== 'application/pdf') {
        setError('Apenas arquivos PDF são permitidos.');
        return;
      }
      if (selectedFile.size > 10 * 1024 * 1024) {
        setError('O arquivo deve ter no máximo 10MB.');
        return;
      }
      setFile(selectedFile);
      setError('');
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      if (droppedFile.type !== 'application/pdf') {
        setError('Apenas arquivos PDF são permitidos.');
        return;
      }
      setFile(droppedFile);
      setError('');
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Por favor, selecione um arquivo PDF.');
      return;
    }

    setUploading(true);
    setError('');

    try {
      const response = await parseOfferingsFile(file, (progressEvent) => {
        // Progress feedback could be added here
      });

      if (response.data.success) {
        if (response.data.data.length === 0) {
          // Mostra debug logs se houver
          let errorMsg = 'Nenhum dado foi encontrado no PDF. Verifique se o arquivo está no formato correto.';
          if (response.data.debug && response.data.debug.length > 0) {
            errorMsg += '\n\nDebug Info:\n' + response.data.debug.join('\n');
            console.log('DEBUG LOGS:', response.data.debug);
          }
          setError(errorMsg);
          setUploading(false);
          return;
        }
        
        // Log debug info no console mesmo se houver sucesso
        if (response.data.debug && response.data.debug.length > 0) {
          console.log('DEBUG LOGS:', response.data.debug);
        }
        
        setParsedData(response.data);
        setEditableData(response.data.data);
        setPeriod(response.data.period);
        setAssociationName(response.data.associationName || 'Associação');
        setStep(2);
      } else {
        let errorMsg = response.data.error || 'Erro ao processar PDF. Verifique se o arquivo está corrompido ou em formato incompatível.';
        
        // Adiciona debug logs se houver
        if (response.data.debug && response.data.debug.length > 0) {
          errorMsg += '\n\nDebug Info:\n' + response.data.debug.join('\n');
          console.log('DEBUG LOGS:', response.data.debug);
        }
        
        setError(errorMsg);
      }
    } catch (error) {
      console.error('Erro ao fazer upload:', error);
      if (error.code === 'ECONNABORTED') {
        setError('Tempo de processamento excedido. O arquivo pode ser muito grande ou complexo.');
      } else if (error.response?.status === 413) {
        setError('Arquivo muito grande. O tamanho máximo permitido é 10MB.');
      } else if (error.response?.status === 500) {
        setError('Erro no servidor ao processar o arquivo. Tente novamente ou entre em contato com o suporte.');
      } else {
        setError(error.response?.data?.message || 'Erro ao processar arquivo. Verifique sua conexão e tente novamente.');
      }
    } finally {
      setUploading(false);
    }
  };

  const handleMinistryChange = (index, ministryId) => {
    const updated = [...editableData];
    const selectedMinistry = ministries.find(m => m._id === ministryId);
    updated[index].ministryId = ministryId;
    updated[index].ministryName = selectedMinistry?.name || '';
    updated[index].currentBalance = selectedMinistry?.currentBalance || 0;
    updated[index].status = ministryId ? 'matched' : 'review';
    setEditableData(updated);
  };

  const handleValueChange = (index, value) => {
    const updated = [...editableData];
    const numValue = parseFloat(value) || 0;
    updated[index].newBalance = numValue;
    setEditableData(updated);
  };

  const handleRemoveRow = (index) => {
    const updated = editableData.filter((_, i) => i !== index);
    setEditableData(updated);
  };

  const calculateDifference = (item) => {
    return item.newBalance - item.currentBalance;
  };

  const handleReview = () => {
    const hasUnmatched = editableData.some(item => !item.ministryId);
    if (hasUnmatched) {
      setError('Existem ministérios não selecionados. Por favor, selecione todos os ministérios ou remova as linhas.');
      return;
    }
    setError('');
    setStep(3);
  };

  const handleConfirm = async () => {
    // Validações antes de importar
    if (!associationName.trim()) {
      setError('Por favor, forneça o nome da associação.');
      return;
    }

    if (period.month < 1 || period.month > 12) {
      setError('Mês inválido. Digite um valor entre 1 e 12.');
      return;
    }

    if (period.year < 2020 || period.year > 2099) {
      setError('Ano inválido. Digite um valor entre 2020 e 2099.');
      return;
    }

    setImporting(true);
    setError('');

    try {
      const response = await confirmOfferingsImport({
        data: editableData,
        period,
        associationName
      });

      setImportResult(response.data);
      setStep(4);
    } catch (error) {
      console.error('Erro ao importar:', error);
      if (error.response?.status === 400) {
        setError('Dados inválidos. Verifique se todos os campos estão preenchidos corretamente.');
      } else if (error.response?.status === 404) {
        setError('Um ou mais ministérios não foram encontrados. Recarregue a página e tente novamente.');
      } else if (error.response?.status === 500) {
        setError('Erro no servidor ao processar a importação. Entre em contato com o suporte se o problema persistir.');
      } else if (error.code === 'ECONNABORTED') {
        setError('Tempo de processamento excedido. Tente dividir a importação em lotes menores.');
      } else {
        setError(error.response?.data?.message || 'Erro ao importar saldos. Verifique sua conexão e tente novamente.');
      }
    } finally {
      setImporting(false);
    }
  };

  const handleReset = () => {
    setStep(1);
    setFile(null);
    setParsedData(null);
    setEditableData([]);
    setImportResult(null);
    setError('');
  };

  return (
    <div className="admin-page">
      <div className="container">
        <div className="page-header">
          <h1>Importar Saldos dos Ministérios</h1>
        </div>

        {/* Progress Indicator */}
        <div className="wizard-progress">
          <div className={`wizard-step ${step >= 1 ? 'active' : ''} ${step > 1 ? 'completed' : ''}`}>
            <div className="wizard-step-number">1</div>
            <div className="wizard-step-label">Upload</div>
          </div>
          <div className={`wizard-step ${step >= 2 ? 'active' : ''} ${step > 2 ? 'completed' : ''}`}>
            <div className="wizard-step-number">2</div>
            <div className="wizard-step-label">Revisão</div>
          </div>
          <div className={`wizard-step ${step >= 3 ? 'active' : ''} ${step > 3 ? 'completed' : ''}`}>
            <div className="wizard-step-number">3</div>
            <div className="wizard-step-label">Confirmação</div>
          </div>
          <div className={`wizard-step ${step >= 4 ? 'active' : ''}`}>
            <div className="wizard-step-number">4</div>
            <div className="wizard-step-label">Resultado</div>
          </div>
        </div>

        {/* Step 1: Upload */}
        {step === 1 && (
          <div className="wizard-content">
            <div className="card">
              <h2>Selecione o arquivo PDF</h2>
              <p className="text-muted">
                Faça upload do relatório PDF de saldo dos departamentos para atualizar os saldos automaticamente.
              </p>

              <div className="alert alert-info">
                <strong>Formato esperado do PDF:</strong>
                <ul>
                  <li>Relatório de "Saldo do Departamento" da associação</li>
                  <li>Cada linha deve representar um ministério com seu saldo final</li>
                  <li>O sistema extrairá o nome da associação e o período automaticamente</li>
                  <li>Os saldos serão atualizados com base no "Saldo Final" de cada ministério</li>
                </ul>
              </div>

              <div
                className={`upload-area ${file ? 'has-file' : ''}`}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
              >
                {!file ? (
                  <>
                    <div className="upload-icon"><FontAwesomeIcon icon={faFilePdf} /></div>
                    <p>Arraste e solte o arquivo PDF aqui</p>
                    <p className="text-muted">ou</p>
                    <label className="btn btn-primary">
                      Selecionar Arquivo
                      <input
                        type="file"
                        accept="application/pdf"
                        onChange={handleFileChange}
                        style={{ display: 'none' }}
                      />
                    </label>
                    <p className="text-muted upload-hint">Tamanho máximo: 10MB • Apenas arquivos PDF</p>
                  </>
                ) : (
                  <>
                    <div className="upload-icon upload-icon-success"><FontAwesomeIcon icon={faCircleCheck} /></div>
                    <p><strong>{file.name}</strong></p>
                    <p className="text-muted">{(file.size / 1024).toFixed(2)} KB</p>
                    <button onClick={() => setFile(null)} className="btn btn-secondary btn-sm">
                      Remover
                    </button>
                  </>
                )}
              </div>

              {error && (
                <div className="alert alert-danger">
                  {error}
                </div>
              )}

              <div className="wizard-actions">
                <button onClick={() => navigate('/admin/ministries')} className="btn btn-secondary">
                  Cancelar
                </button>
                <button
                  onClick={handleUpload}
                  className="btn btn-primary"
                  disabled={!file || uploading}
                >
                  {uploading ? 'Processando...' : 'Próximo'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Review */}
        {step === 2 && (
          <div className="wizard-content">
            <div className="card">
              <h2>Revisar Saldos Extraídos</h2>
              <p className="text-muted">
                Revise os saldos extraídos e ajuste conforme necessário. O sistema calculará a diferença entre o saldo atual e o novo saldo.
              </p>

              <div className="alert alert-info">
                <strong>Associação Detectada:</strong> {associationName}
                <br />
                <small>Este nome será usado no log dos ajustes: "Novo Saldo - {associationName}"</small>
              </div>

              <div className="alert alert-warning">
                <strong>Instruções de Revisão:</strong>
                <ul>
                  <li>Linhas marcadas como "Revisar" precisam de atenção - selecione o ministério correto</li>
                  <li>Verifique se os saldos finais estão corretos antes de prosseguir</li>
                  <li>Use o botão de remover para excluir linhas que não devem ser atualizadas</li>
                  <li>Todos os ministérios devem estar selecionados antes de continuar</li>
                </ul>
              </div>

              {parsedData && (
                <div className="import-summary">
                  <div className="summary-item">
                    <strong>Período:</strong> {parsedData.period.month}/{parsedData.period.year}
                  </div>
                  <div className="summary-item">
                    <strong>Total de registros:</strong> {editableData.length}
                  </div>
                  <div className="summary-item">
                    <strong>Mapeados:</strong> {editableData.filter(d => d.status === 'matched').length}
                  </div>
                  <div className="summary-item">
                    <strong>Revisar:</strong> {editableData.filter(d => d.status === 'review').length}
                  </div>
                </div>
              )}

              <div className="table-responsive">
                <table className="import-table">
                  <thead>
                    <tr>
                      <th>Nome Extraído</th>
                      <th>Ministério</th>
                      <th>Saldo Atual</th>
                      <th>Novo Saldo</th>
                      <th>Diferença</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {editableData.map((item, index) => {
                      const difference = calculateDifference(item);
                      return (
                        <tr key={index} className={item.status === 'review' ? 'needs-review' : ''}>
                          <td>
                            <strong>{item.extractedName}</strong>
                            {item.status === 'review' && (
                              <span
                                className="badge badge-warning"
                                title="Não foi possível associar automaticamente. Selecione o ministério correto."
                              >
                                <FontAwesomeIcon icon={faTriangleExclamation} /> Revisar
                              </span>
                            )}
                          </td>
                          <td>
                            <select
                              className="form-control"
                              value={item.ministryId || ''}
                              onChange={(e) => handleMinistryChange(index, e.target.value)}
                            >
                              <option value="">Selecione...</option>
                              {ministries.map(ministry => (
                                <option key={ministry._id} value={ministry._id}>
                                  {ministry.name}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td>
                            <span className="balance-value">
                              {formatAccountingCurrency(item.currentBalance)}
                            </span>
                          </td>
                          <td>
                            <input
                              type="number"
                              className="form-control"
                              value={item.newBalance}
                              onChange={(e) => handleValueChange(index, e.target.value)}
                              step="0.01"
                            />
                          </td>
                          <td>
                            <span className={`difference-value ${difference >= 0 ? 'positive' : 'negative'}`}>
                              {difference >= 0 ? '+' : ''}{formatAccountingCurrency(difference)}
                            </span>
                          </td>
                          <td>
                            <button
                              onClick={() => handleRemoveRow(index)}
                              className="btn btn-sm btn-danger"
                              title="Remover esta linha da importação"
                              aria-label="Remover linha"
                            >
                              <FontAwesomeIcon icon={faXmark} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {error && (
                <div className="alert alert-danger">
                  {error}
                </div>
              )}

              <div className="wizard-actions">
                <button onClick={handleReset} className="btn btn-secondary">
                  Voltar
                </button>
                <button onClick={handleReview} className="btn btn-primary">
                  Próximo
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Confirmation */}
        {step === 3 && (
          <div className="wizard-content">
            <div className="card">
              <h2>Confirmar Atualização de Saldos</h2>
              <p className="text-muted">
                Revise as informações finais antes de confirmar a atualização dos saldos.
              </p>

              <div className="confirmation-details">
                <div className="form-group">
                  <label className="form-label">Período</label>
                  <div className="period-selector">
                    <input
                      type="number"
                      className="form-control"
                      value={period.month}
                      onChange={(e) => setPeriod({ ...period, month: parseInt(e.target.value) })}
                      min="1"
                      max="12"
                      placeholder="Mês"
                    />
                    <span>/</span>
                    <input
                      type="number"
                      className="form-control"
                      value={period.year}
                      onChange={(e) => setPeriod({ ...period, year: parseInt(e.target.value) })}
                      min="2020"
                      max="2099"
                      placeholder="Ano"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Nome da Associação</label>
                  <input
                    type="text"
                    className="form-control"
                    value={associationName}
                    onChange={(e) => setAssociationName(e.target.value)}
                    placeholder="Ex: Associação Norte do Rio Grande do Sul"
                  />
                  <small className="form-text">
                    Este nome será usado no log: "Novo Saldo - {associationName}"
                  </small>
                </div>

                <div className="confirmation-summary">
                  <h3>Resumo da Atualização</h3>
                  <table className="summary-table">
                    <thead>
                      <tr>
                        <th>Ministério</th>
                        <th>Saldo Atual</th>
                        <th>Novo Saldo</th>
                        <th>Ajuste</th>
                      </tr>
                    </thead>
                    <tbody>
                      {editableData.map((item, index) => {
                        const difference = calculateDifference(item);
                        return (
                          <tr key={index}>
                            <td>{item.ministryName}</td>
                            <td>{formatAccountingCurrency(item.currentBalance)}</td>
                            <td>{formatAccountingCurrency(item.newBalance)}</td>
                            <td className={difference >= 0 ? 'positive' : 'negative'}>
                              {difference >= 0 ? '+' : ''}{formatAccountingCurrency(difference)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="alert alert-warning">
                  <strong><FontAwesomeIcon icon={faTriangleExclamation} /> Atenção:</strong> Ao confirmar, os saldos dos ministérios serão atualizados conforme mostrado acima. 
                  Um ajuste será criado automaticamente no histórico de cada ministério com a descrição "Novo Saldo - {associationName}". 
                  Revise cuidadosamente todos os valores antes de prosseguir.
                </div>
              </div>

              {error && (
                <div className="alert alert-danger">
                  {error}
                </div>
              )}

              <div className="wizard-actions">
                <button onClick={() => setStep(2)} className="btn btn-secondary" disabled={importing}>
                  Voltar
                </button>
                <button onClick={handleConfirm} className="btn btn-primary" disabled={importing}>
                  {importing ? 'Atualizando...' : 'Confirmar Atualização'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Step 4: Result */}
        {step === 4 && importResult && (
          <div className="wizard-content">
            <div className="card">
              <div className={`result-icon ${importResult.summary.errors === 0 ? 'result-icon-success' : 'result-icon-warning'}`}>
                <FontAwesomeIcon icon={importResult.summary.errors === 0 ? faCircleCheck : faTriangleExclamation} />
              </div>
              <h2>
                {importResult.summary.errors === 0 ? 'Atualização Concluída com Sucesso!' : 'Atualização Concluída com Avisos'}
              </h2>

              <div className="result-summary">
                <div className="result-stat">
                  <div className="result-stat-value">{importResult.summary.total}</div>
                  <div className="result-stat-label">Total</div>
                </div>
                <div className="result-stat success">
                  <div className="result-stat-value">{importResult.summary.success}</div>
                  <div className="result-stat-label">Sucesso</div>
                </div>
                <div className="result-stat error">
                  <div className="result-stat-value">{importResult.summary.errors}</div>
                  <div className="result-stat-label">Erros</div>
                </div>
              </div>

              {importResult.results.success.length > 0 && (
                <div className="result-section">
                  <h3>Saldos Atualizados com Sucesso</h3>
                  <table className="result-table">
                    <thead>
                      <tr>
                        <th>Ministério</th>
                        <th>Saldo Anterior</th>
                        <th>Novo Saldo</th>
                        <th>Ajuste Aplicado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importResult.results.success.map((item, index) => (
                        <tr key={index}>
                          <td>{item.ministry}</td>
                          <td>{formatAccountingCurrency(item.previousBalance)}</td>
                          <td><strong>{formatAccountingCurrency(item.newBalance)}</strong></td>
                          <td className={item.adjustment >= 0 ? 'positive' : 'negative'}>
                            {item.adjustment >= 0 ? '+' : ''}{formatAccountingCurrency(item.adjustment)}
                            {item.note && <small className="text-muted"> ({item.note})</small>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {importResult.results.errors.length > 0 && (
                <div className="result-section">
                  <h3>Erros Encontrados</h3>
                  <ul className="error-list">
                    {importResult.results.errors.map((error, index) => (
                      <li key={index}>
                        <strong>{error.ministry}:</strong> {error.error}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="wizard-actions">
                <button onClick={() => navigate('/admin/ministries')} className="btn btn-secondary">
                  Ir para Ministérios
                </button>
                <button onClick={handleReset} className="btn btn-primary">
                  Nova Importação
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ImportOfferings;
